//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';

// === COMPONENTS ===
import { Document } from './components/Document';

// === API ===
import { emit, on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

const DocMemo = React.memo(Document);

//-----------------------------------------------------------------
export function Thumbnails(props) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { fill } = props;

  const theme = useTheme();
  const panel_ref              = useRef( null );
  const [ focused_doc_id, setFocusedDocId ] = useState( null );
  const raf_ref                = useRef( 0 );
  const last_wheel_ts_ref      = useRef( 0 ); // track last user wheel interaction

  const [documents, setDocuments] = useState({ items: {}, order: [] });
  const [images, setImages] = useState({ items: {}, order: [] });

  const [selected_thumb_id, setSelectedThumbId] = useState(null);
  const selected_thumb_id_ref = useRef(null);

  //#endregion

  //#region [STYLES]

  const _styles = {
    thumb_paper: {
      gridColumn: fill ? `span ${fill}` : 'span 2',
      overflow: 'auto',
      position: 'relative',
      height: '100%',
      width: '100%',
      boxShadow: 0,
      backgroundColor: theme.palette.background.secondary[theme.palette.mode],
    },
    document_container: {
      height: '100%', width: '100%', overflow: 'visible',
    }
  }

  //#endregion

  //#region [EFFECT]

    useEffect(() => {
    function handleDocFromImage(data) {
      setFocusedDocId(data);
    }

    on('DOCUMENT-ID', handleDocFromImage);

    return () => {
      removeListener('DOCUMENT-ID', handleDocFromImage);
    };
  }, []);

  useEffect(() => {

    //-----------------------------------------------------------------
    function handleDocuments(data) {
      //-----------------------------------------------------------------
      setDocuments(data);
    }

    //-----------------------------------------------------------------
    function handleImages(data) {
      //-----------------------------------------------------------------
      setImages(data);
    }

    //-----------------------------------------------------------------
    function handleImageUpdates(data) {
      //-----------------------------------------------------------------
      if (data.images?.order) {
        setImages(prev => ({
          ...prev,
          order: data.images.order
        }));
      }

      data.images?.update?.forEach(image => {
        emit('IMAGE-' + image.id, image);
      });
    }

    //-----------------------------------------------------------------
    function handleClear(data) {
      //-----------------------------------------------------------------
      setImages({ items: {}, order: [] });
      setDocuments({ items: {}, order: [] });
    }

    on('TASK_DOCUMENTS', handleDocuments);
    on('TASK_IMAGES', handleImages);
    on('IMAGE_UPDATE', handleImageUpdates);
    on('THUMBS_CLEAR', handleClear);

    return () => {
      removeListener('TASK_DOCUMENTS', handleDocuments);
      removeListener('TASK_IMAGES', handleImages);
      removeListener('IMAGE_UPDATE', handleImageUpdates);
      removeListener('THUMBS_CLEAR', handleClear);
    }

  }, []);

  useEffect(() => {
    //-----------------------------------------------------------------
    function unsubscribe() {
      //-----------------------------------------------------------------
      sendMessageData('TASK_VIEW_THUMBS_UNSUBSCRIBE');
    }

    sendMessageData('TASK_VIEW_THUMBS_SUBSCRIBING');

    window.addEventListener('beforeunload', unsubscribe);
    return () => {
      window.removeEventListener('beforeunload', unsubscribe);
    }
  }, []);

  useEffect(() => {
    function _handleReconnect(data) {
      sendMessageData('TASK_VIEW_THUMBS_SUBSCRIBING', data);
    }

    on('connection', _handleReconnect);

    return () => {
      removeListener('connection', _handleReconnect);
    }
  }, []);

  useEffect(() => {
    //-----------------------------------------------------------------
    function handleDocumentUpdates(data) {
      //-----------------------------------------------------------------
      try {
        setDocuments(prev => {
          const temp = {
            items: { ...prev.items },
            order: prev.order
          };

          if (data.documents?.order) {
            temp.order = data.documents.order;
          }

          data.documents?.create?.forEach(doc => {
            temp.items[doc.Id] = doc;
          });

          data.documents?.update?.forEach(doc => {
            temp.items[doc.Id] = {
              ...doc,
              timestamp: Date.now()
            };
          });

          data.documents?.remove?.forEach(id => {
            delete temp.items[id];
          });

          return temp;
        });
      } catch (error) {
        console.warn(error);
      }
    }

    on('DOCUMENT_UPDATE', handleDocumentUpdates);

    return () => {
      removeListener('DOCUMENT_UPDATE', handleDocumentUpdates);
    }
  }, []);

useEffect(() => {
  selected_thumb_id_ref.current = selected_thumb_id;
}, [selected_thumb_id]);

  useEffect(() => {
    const container = panel_ref.current;
    if (!container) return;

    function shouldHandleWheel(e) {
      const viewportHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;
      const hasScrollbar = scrollHeight > viewportHeight + 1;

      if (!hasScrollbar) return true;

      const scrollTop = container.scrollTop;
      const maxScroll = scrollHeight - viewportHeight;

      const atTop = scrollTop <= 2;
      const atBottom = scrollTop >= maxScroll - 2;

      const scrollingDown = e.deltaY > 0;
      const scrollingUp = e.deltaY < 0;

      return (
        (atTop && scrollingUp) ||
        (atBottom && scrollingDown)
      );
    }

    function handleScroll() {
      if (raf_ref.current) return;

      // Ignore scroll events that were not triggered by a recent user wheel.
      // This prevents jitter when scrollIntoView() or other programmatic
      // scrolling occurs after field acceptance or focus changes.
      if (Date.now() - last_wheel_ts_ref.current > 200) return;

      raf_ref.current = requestAnimationFrame(() => {
        raf_ref.current = 0;

        const scrollTop = container.scrollTop;
        const viewportHeight = container.clientHeight;
        const scrollHeight = container.scrollHeight;
        const hasScrollbar = scrollHeight > viewportHeight + 1;

        // If no scrollbar exists, scroll selection is handled by wheel
        if (!hasScrollbar) return;

        const maxScroll = scrollHeight - viewportHeight;
        const scrollRatio = maxScroll > 0
          ? Math.min(1, Math.max(0, scrollTop / maxScroll))
          : 0;

        const containerRect = container.getBoundingClientRect();

        const anchorOffset = scrollRatio * viewportHeight;
        const dynamicY = containerRect.top + anchorOffset;

        let closestId = null;
        let smallestDistance = Infinity;

        const thumbs = container.querySelectorAll('[data-thumb-id]');
        if (!thumbs.length) return;

        thumbs.forEach((el) => {
          const rect = el.getBoundingClientRect();
          const thumbCenter = rect.top + rect.height / 2;
          const distance = Math.abs(dynamicY - thumbCenter);

          if (distance < smallestDistance) {
            smallestDistance = distance;
            closestId = el.getAttribute('data-thumb-id');
          }
        });

        if (closestId && closestId !== selected_thumb_id_ref.current) {
          setSelectedThumbId(closestId);
        }
      });
    }

    container.addEventListener('scroll', handleScroll, { passive: true });

    function handleWheel(e) {
      last_wheel_ts_ref.current = Date.now();

      if (!shouldHandleWheel(e)) return;

      e.preventDefault();

      const thumbs = Array.from(container.querySelectorAll('[data-thumb-id]'));
      if (!thumbs.length) return;

      const order = thumbs.map(el => el.getAttribute('data-thumb-id'));

      const currentId = selected_thumb_id_ref.current;
      let currentIndex = order.indexOf(currentId);

      if (currentIndex === -1) {
        setSelectedThumbId(e.deltaY > 0 ? order[0] : order[order.length - 1]);
        return;
      }

      if (e.deltaY > 0 && currentIndex < order.length - 1) {
        setSelectedThumbId(order[currentIndex + 1]);
      } else if (e.deltaY < 0 && currentIndex > 0) {
        setSelectedThumbId(order[currentIndex - 1]);
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: false });

    // Run once initially
    handleScroll();

    // If no scrollbar and nothing selected yet, select first item
    const viewportHeight = container.clientHeight;
    const scrollHeight = container.scrollHeight;
    const hasScrollbarInit = scrollHeight > viewportHeight + 1;
    if (!hasScrollbarInit && !selected_thumb_id_ref.current) {
      const thumbs = Array.from(container.querySelectorAll('[data-thumb-id]'));
      if (thumbs.length) {
        setSelectedThumbId(thumbs[0].getAttribute('data-thumb-id'));
      }
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      if (raf_ref.current) {
        cancelAnimationFrame(raf_ref.current);
        raf_ref.current = 0;
      }
    };
  }, [documents.order, images.order]);

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box ref={panel_ref} sx={_styles.thumb_paper}>
        <Box sx={_styles.document_container}>
          {documents.order.map(displayDocument)}
        </Box>
      </Box>
    );
  }

  //-----------------------------------------------------------------
  function displayDocument(id, index) {
    //-----------------------------------------------------------------
    const document = documents.items[id];
    if (!document) return null;
    const timestamp = document?.timestamp;
    return (
      <Box key={'d' + id}>
        <DocMemo
          key={'d' + id}
          id={id}
          timestamp={timestamp}
          index={index}
          images={images.order}
          scroll_selected_id={selected_thumb_id}
          onThumbSelect={setSelectedThumbId}
          focused_doc_id={ focused_doc_id }
        />
      </Box>
    );
  }

  //#endregion

  return display();
}
